import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Upload, RotateCcw, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminDataSnapshotPage
 * ----------------------
 * Super-Admin-only data sync utility.
 *
 * Download: pulls every non-temp MongoDB collection from THIS environment as
 * a single gzipped NDJSON file. Use on production to capture state.
 *
 * Restore : uploads a snapshot file produced by Download and replaces the
 * current environment's collections with its contents. Use on preview to
 * mirror production exactly. Pre-restore backup is automatic; the returned
 * `backup_tag` can be used to rollback in one click.
 */
const AdminDataSnapshotPage = () => {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [lastBackupTag, setLastBackupTag] = useState(null);
  const [restoreSummary, setRestoreSummary] = useState(null);
  const [confirmRestoreText, setConfirmRestoreText] = useState('');
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/system/snapshot/download`,
        { headers: getAuthHeaders(), responseType: 'blob', timeout: 600000 }
      );
      const ts = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
      const filename = `girgit_snapshot_${ts}.ndjson.gz`;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Snapshot downloaded: ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Snapshot download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async (file) => {
    if (!file) return;
    if (confirmRestoreText !== 'REPLACE') {
      toast.error('Type REPLACE in the confirmation box first.');
      return;
    }
    setRestoring(true);
    setRestoreSummary(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post(
        `${API}/api/admin/system/snapshot/restore`,
        form,
        {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
        }
      );
      setLastBackupTag(response.data.backup_tag);
      setRestoreSummary(response.data.summary);
      setConfirmRestoreText('');
      toast.success(
        `Restore complete. Backup tag: ${response.data.backup_tag}. ` +
        `${Object.keys(response.data.summary).length} collections restored.`
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Restore failed.');
    } finally {
      setRestoring(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRollback = async () => {
    if (!lastBackupTag) return;
    if (!window.confirm(`Roll back to backup ${lastBackupTag}? This UNDOES the last restore.`)) return;
    setRestoring(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/system/snapshot/rollback`,
        { backup_tag: lastBackupTag },
        { headers: getAuthHeaders() }
      );
      toast.success(`Rolled back ${response.data.count} collections.`);
      setLastBackupTag(null);
      setRestoreSummary(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rollback failed.');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="data-snapshot-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Data Snapshot</h1>
        <p className="text-sm text-gray-600 mt-1">
          One-click full-database export &amp; import. Use to mirror production
          data into preview (or any 1:1 environment sync).
        </p>
      </div>

      {/* DOWNLOAD CARD */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Download className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium text-gray-900">Download Snapshot</h2>
            <p className="text-sm text-gray-600 mt-1">
              Exports every non-temp collection (employees, clients, tasks, events,
              leaves, invoices, audit logs, notifications, etc.) as a single
              gzipped <code className="font-mono text-xs">.ndjson.gz</code> file.
              Uploaded file blobs in object storage are NOT included.
            </p>
            <button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="snapshot-download-btn"
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-wait"
            >
              {downloading ? 'Preparing snapshot…' : 'Download full DB snapshot'}
            </button>
          </div>
        </div>
      </div>

      {/* RESTORE CARD */}
      <div className="bg-white border border-amber-200 rounded-lg p-6 mb-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-50 rounded-lg">
            <Upload className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium text-gray-900">Restore from Snapshot</h2>
            <p className="text-sm text-gray-600 mt-1">
              Replaces this environment&apos;s collections with the contents of an uploaded
              snapshot file. Existing data is auto-backed up before overwrite — you
              can rollback in one click if anything goes wrong.
            </p>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-900">
                <strong>This overwrites data.</strong> Only use on preview / staging
                environments. Type <code className="font-mono font-bold">REPLACE</code> in
                the box below to confirm before selecting a file.
              </p>
            </div>
            <div className="mt-4 flex gap-3 items-center">
              <input
                type="text"
                value={confirmRestoreText}
                onChange={(e) => setConfirmRestoreText(e.target.value)}
                placeholder='Type "REPLACE" to enable'
                data-testid="snapshot-confirm-input"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".gz,.ndjson"
                onChange={(e) => handleRestore(e.target.files?.[0])}
                disabled={confirmRestoreText !== 'REPLACE' || restoring}
                data-testid="snapshot-restore-file"
                className="text-sm"
              />
            </div>
            {restoring && (
              <p className="text-sm text-gray-600 mt-3">Restoring… do not close this tab.</p>
            )}
          </div>
        </div>
      </div>

      {/* ROLLBACK CARD */}
      {lastBackupTag && (
        <div className="bg-white border border-green-200 rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <RotateCcw className="w-6 h-6 text-green-700" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-medium text-gray-900">
                Rollback Last Restore
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Backup tag: <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{lastBackupTag}</code>
              </p>
              <button
                onClick={handleRollback}
                disabled={restoring}
                data-testid="snapshot-rollback-btn"
                className="mt-4 px-4 py-2 bg-green-700 text-white rounded-md text-sm font-medium hover:bg-green-800 disabled:opacity-50"
              >
                Rollback to pre-restore state
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUMMARY */}
      {restoreSummary && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Last Restore Summary</h3>
          <table className="w-full text-sm" data-testid="snapshot-restore-summary">
            <thead>
              <tr className="text-left border-b border-gray-200 text-gray-500 text-xs uppercase">
                <th className="py-2">Collection</th>
                <th className="py-2 text-right">Documents Inserted</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(restoreSummary).map(([coll, info]) => (
                <tr key={coll} className="border-b border-gray-100">
                  <td className="py-2 font-mono text-xs">{coll}</td>
                  <td className="py-2 text-right">
                    {info.error ? (
                      <span className="text-red-600">{info.error}</span>
                    ) : (
                      info.inserted
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminDataSnapshotPage;
